import * as Sentry from '@sentry/react';
import { incidencePaths, kardexPaths, usersPaths } from 'constants/routePaths';
import { redirectAfterLogin } from 'hooks/redirectAfterLogin';
import { useAppDispatch } from 'hooks/reduxHooks';
import notificationHook from 'hooks/notificationHook';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import statusMessages from 'statusMessages';
import { doImpersonate } from 'store/slices/authSlice';
import { doDelete, doUpdateOne } from 'store/slices/userSlice';
import { ApiError, getApiErrorMessage } from 'types';
import { EmployeeActions } from 'types/employeeActions';
import User from 'types/user';

interface UseEmployeeActionsOptions {
  onDeleteSuccess?: () => void;
}

export const useEmployeeActions = ({
  onDeleteSuccess,
}: UseEmployeeActionsOptions = {}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [impersonatingEmployeeId, setImpersonatingEmployeeId] = useState<
    string | null
  >(null);

  const onDelete = useCallback(
    async (employee: User) => {
      try {
        await dispatch(doDelete(employee._id)).unwrap();
        notificationHook({
          type: 'success',
          message: 'Usuario eliminado con éxito',
        });
        onDeleteSuccess?.();
      } catch (error) {
        const currentStatus = (error as ApiError).statusCode;
        const fallbackMessage = statusMessages.user[
          currentStatus ? currentStatus : 500
        ] as string;
        notificationHook({
          message: getApiErrorMessage(error, fallbackMessage),
          type: 'error',
        });
      }
    },
    [dispatch, onDeleteSuccess]
  );

  const onChangeStatus = useCallback(
    async (employee: User) => {
      try {
        await dispatch(
          doUpdateOne({ _id: employee._id, status: !employee.status })
        ).unwrap();
      } catch (error) {
        const currentStatus = (error as ApiError).statusCode;
        const fallbackMessage = statusMessages.user[
          currentStatus ? currentStatus : 500
        ] as string;
        notificationHook({
          message: getApiErrorMessage(error, fallbackMessage),
          type: 'error',
        });
      }
    },
    [dispatch]
  );

  const onImpersonate = useCallback(
    async (employee: User) => {
      setImpersonatingEmployeeId(employee._id);
      try {
        const response = await dispatch(doImpersonate(employee._id)).unwrap();
        if (response.user) {
          navigate(redirectAfterLogin(response.user.role));
        }
      } catch (error) {
        Sentry.setContext('Props', employee);
        Sentry.captureException(error as Error);
        const currentStatus = (error as ApiError).statusCode;
        notificationHook({
          message: statusMessages.auth[
            currentStatus ? currentStatus : 500
          ] as string,
          type: 'error',
          duration: 5,
        });
      } finally {
        setImpersonatingEmployeeId(null);
      }
    },
    [dispatch, navigate]
  );

  const onOpenDetail = useCallback(
    (employee: User) => {
      navigate(`${usersPaths.userDetail}/${employee._id}`);
    },
    [navigate]
  );
  const onEdit = useCallback(
    (employee: User) => {
      navigate(`${usersPaths.editUser}/${employee._id}`);
    },
    [navigate]
  );
  const onOpenContracts = useCallback(
    (employee: User) => {
      navigate(`${usersPaths.userContracts}/${employee._id}`);
    },
    [navigate]
  );
  const onOpenKardex = useCallback(
    (employee: User) => {
      navigate(`${kardexPaths.base}/${employee._id}`);
    },
    [navigate]
  );
  const onReconcileIncidences = useCallback(
    (employee: User) => {
      navigate(
        `${incidencePaths.basePath}/${incidencePaths.reconcile}/${employee._id}`
      );
    },
    [navigate]
  );

  const actions: EmployeeActions = useMemo(
    () => ({
      onChangeStatus,
      onDelete,
      onEdit,
      onImpersonate,
      onOpenContracts,
      onOpenDetail,
      onOpenKardex,
      onReconcileIncidences,
    }),
    [
      onChangeStatus,
      onDelete,
      onEdit,
      onImpersonate,
      onOpenContracts,
      onOpenDetail,
      onOpenKardex,
      onReconcileIncidences,
    ]
  );

  return { actions, impersonatingEmployeeId };
};
